import type { PayloadAction } from '@reduxjs/toolkit';
import { LIMIT_ATTACH_IMAGES } from '@wix/wix-chatbot-common/attachments';
import { WidgetClosingTrigger } from '@wix/wix-chatbot-common/bi';
import type { NavigationParams } from '@wix/wix-chatbot-common/browser';
import type { Store } from 'redux';

import { ACTIONS, EVENTS_LIVE_CHAT, INTERNAL_MESSAGE, MESSAGES } from '../../constants';
import type { IMeta, IStartChatData, TMessages } from '../../server/service/InitialChatData';
import type { Agent, StatusBanner } from '../../types/domain';
import { isObject, isString, isUndefined } from '../../util/guards';
import { sendNavigatedAction } from '../common/api/sendNavigatedAction';
import { sendRestartChat } from '../common/api/sendRestartChat';
import type { TRootState } from '../common/reducers';
import { chatActions } from '../common/reducers/chat';
import { fieldActions } from '../common/reducers/field';
import { headerActions } from '../common/reducers/header';
import { IMetaUpdateIntentId, IModalOpenAction, ResetReason, TMetaActions } from '../common/reducers/types';
import { selectRestartChatParameters } from '../common/selectors/chat';
import { selectEditorType } from '../common/selectors/homepage';
import { selectLoadPageUrl } from '../common/selectors/meta';
import { loadAttaches } from '../common/thunks/attaches.thunk';
import { widgetSDK } from '../sdk/widgetSDK';
import { cleanDeferredAction, setDeferredAction } from '../util/deferredAction';
import { liveChatWaitingCheck } from '../util/liveChatWaitingCheck';
import { navigationControl } from '../util/navigationControl';
import { renderModel } from '../util/renderModel';
import { createChatMessage, isLiveChatMode } from './chatCreators';

export type TAction = PayloadAction<any> | IModalOpenAction | TMetaActions;

export interface TInternalAction {
  (): TAction | PayloadAction<any> | undefined;
  type?: INTERNAL_MESSAGE;
}

export type TMessage = TAction | TInternalAction;

const { isWidget, isCopilot } = renderModel.get();

const setIntentId = (intentId: string, mainOptionName: string): IMetaUpdateIntentId => ({
  intentId,
  mainOptionName,
  type: ACTIONS.META_UPDATE_INTENT_ID,
});

const createInternalAction = (payload: unknown, store: Store): TInternalAction | undefined => {
  if (!isObject(payload)) {
    return;
  }

  switch (payload.type) {
    // Live Chat
    case MESSAGES.CHANGE_MODE: {
      const isLiveChat = isLiveChatMode(payload.mode);

      return () => {
        if (isLiveChat) {
          liveChatWaitingCheck(store);
        }
        return headerActions.changeChatMode(isLiveChat);
      };
    }

    // Internal messages:
    case INTERNAL_MESSAGE.START: {
      const func = () => {
        navigationControl.unlock();

        const state = store.getState();
        if (state.chat.isReset && state.chat.resetReason === ResetReason.StartOver) {
          return undefined;
        }

        return chatActions.reset(ResetReason.InternalMessage);
      };
      func.type = INTERNAL_MESSAGE.START;
      return func;
    }

    case INTERNAL_MESSAGE.COLLAPSE_WIDGET: {
      const func = (): undefined => {
        if (isWidget) {
          widgetSDK.collapseWidget(WidgetClosingTrigger.ChatbotConversation);
          return;
        }
      };
      func.type = INTERNAL_MESSAGE.COLLAPSE_WIDGET;
      return func;
    }

    case INTERNAL_MESSAGE.CHANGE_RESTART_CHAT_PARAMETERS: {
      const { nodeId } = payload;
      return () =>
        chatActions.changeRestartChatParameters({
          ...(nodeId && isString(nodeId) ? { nodeId } : undefined),
          ...(isCopilot && { referral: store.getState().meta.referral }),
        });
    }

    case INTERNAL_MESSAGE.REDIRECT: {
      return (): undefined => {
        if (!isString(payload.url)) {
          return;
        }

        if (payload.deferred) {
          // In cases when we not in BM deferred action should be triggered after chat loaded
          setDeferredAction();
        }

        if (isWidget) {
          const navigationPromise = widgetSDK.navigateTo({
            ...payload,
            openInNewTab: payload.newTab,
          } as NavigationParams);
          if (navigationPromise) {
            navigationPromise
              .then(sendNavigatedAction(store))
              .then((res) => res && cleanDeferredAction()) // only on BM will have some response
              .catch((_) => {
                sendNavigatedAction(store)({
                  url: String(payload.url),
                  navigationFailed: true,
                });
              });
          }
          return;
        }
        // for old chatbot full page @todo remove this after old chatbot is deprecated
        navigationControl.to(payload.url, (payload.newTab as boolean) || isCopilot);
        if (isCopilot && !payload.newTab) {
          const restartParameters = selectRestartChatParameters(store.getState());
          const loadPageUrl = selectLoadPageUrl(store.getState());
          const editorType = selectEditorType(store.getState());
          sendRestartChat(restartParameters, loadPageUrl, editorType);
          chatActions.reset(ResetReason.StartOver);
        }
      };
    }

    // Live Chat
    case EVENTS_LIVE_CHAT.AGENT_JOINED: {
      return () => headerActions.agentJoined(payload.agent as Agent);
    }

    case EVENTS_LIVE_CHAT.AGENT_LEFT: {
      return () => headerActions.agentLeft(payload.agent as Agent);
    }

    default:
      return;
  }
};

export const createAction = (payload: unknown, store: Store): TMessage[] => {
  const actions: TMessage[] = [];

  const item = createChatMessage(payload);
  const internal = createInternalAction(payload, store);

  if (!isUndefined(item)) {
    actions.push(chatActions.addItem(item));
  }

  if (!isUndefined(internal)) {
    actions.push(internal);
  }

  return actions;
};

export const createMessages = (message: TMessages[], store: Store): TMessage[] => {
  let messages: TMessage[] = [];
  if (Array.isArray(message) && message.length) {
    messages = message.filter(Boolean).reduce((acc: TMessage[], item: unknown) => {
      const actions = createAction(item, store);
      if (Array.isArray(actions)) {
        acc.push(...actions);
      }
      return acc;
    }, []);
  }

  return messages;
};

export const createBefore = (startData?: IStartChatData) => {
  return (): TAction[] => {
    const actions: TAction[] = [chatActions.toggleLock(true)];

    if (isObject(startData)) {
      if (isString(startData.conversationId)) {
        actions.push(chatActions.updateSession({ conversationId: startData.conversationId }));
      }
    }

    return actions;
  };
};

export const createAfter = (
  payload: unknown,
  store: Store<TRootState>,
  meta?: IMeta,
  statusBanner?: StatusBanner,
) => {
  return (): TAction[] => {
    const actions: TAction[] = [chatActions.toggleLock(false)];
    const isLiveChat = store.getState().chat.isLiveChat;

    if (meta?.intentId) {
      const { intentId, mainOptionName } = meta;
      actions.push(setIntentId(intentId, mainOptionName));
    }

    if (isObject(payload) && (payload as { contactFlow?: boolean }).contactFlow) {
      actions.push(chatActions.startConnectingAgent());
    }

    if (statusBanner) {
      actions.push(headerActions.setStatusBanner(statusBanner));
    }

    if (isObject(payload)) {
      const uiUpdates = isLiveChat
        ? fieldActions.updateLiveChatbot(payload)
        : fieldActions.updateChatbot(payload);
      actions.push(uiUpdates);

      // TODO: Refactor action types with async thunks
      if (Array.isArray(payload.attachments)) {
        actions.push({
          type: loadAttaches.fulfilled.type as any,
          payload: payload.attachments.slice(-LIMIT_ATTACH_IMAGES) as any,
        });
      }
    }

    // Cleaning up bot typing if it is the only message left (needed for the no start-message flow)
    actions.push(chatActions.cleanUpBotTyping());

    return actions;
  };
};
